import React, { useEffect, useState, useCallback, useRef } from 'react'
import { supabase } from '../supabaseClient'
import { Grid4, MetricCard, Empty, ZoomBtn } from './Layout'

const STATUSES = ['Applied', 'Screening', '1st Round', '2nd Round', 'Offer', 'Hired', 'Rejected']

const STATUS_META = {
  'Applied':    { bg:'rgba(96,165,250,0.15)',  color:'#60a5fa' },
  'Screening':  { bg:'rgba(251,191,36,0.15)',  color:'#fbbf24' },
  '1st Round':  { bg:'rgba(244,163,53,0.15)',  color:'#f4a335' },
  '2nd Round':  { bg:'rgba(139,92,246,0.15)',  color:'#a78bfa' },
  'Offer':      { bg:'rgba(34,211,238,0.15)',  color:'#22d3ee' },
  'Hired':      { bg:'rgba(52,211,153,0.15)',  color:'#34d399' },
  'Rejected':   { bg:'rgba(248,113,113,0.15)', color:'#f87171' },
}

const RATING_FIELDS = [
  { key:'communication_rating', label:'Communication' },
  { key:'technical_rating',     label:'Technical'      },
  { key:'culture_fit_rating',   label:'Culture Fit'    },
]

const RECOMMENDATIONS = ['Hire', 'Maybe', 'No Hire']

const S = {
  card:  { background:'rgba(var(--fg-rgb),0.03)', border:'0.5px solid rgba(var(--fg-rgb),0.08)', borderRadius:'16px', padding:'20px' },
  input: { padding:'10px 12px', background:'rgba(var(--fg-rgb),0.05)', border:'0.5px solid rgba(var(--fg-rgb),0.1)', borderRadius:'9px', color:'rgba(var(--fg-rgb),var(--a85))', fontSize:'13px', outline:'none', fontFamily:'inherit', boxSizing:'border-box' },
  th: { fontSize:'10px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a30))', letterSpacing:'0.08em', textTransform:'uppercase', padding:'8px 10px', textAlign:'left' },
  td: { fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a75))', padding:'9px 10px' },
  btn: (variant='primary') => ({
    padding:'9px 16px', borderRadius:'9px', fontSize:'12px', fontWeight:700, cursor:'pointer', border:'none', fontFamily:'inherit',
    background: variant==='danger' ? 'rgba(239,68,68,0.15)' : variant==='ghost' ? 'rgba(var(--fg-rgb),0.06)' : '#1e90ff',
    color: variant==='danger' ? '#f87171' : variant==='ghost' ? 'rgba(var(--fg-rgb),var(--a55))' : '#fff',
  }),
}

function fmtDate(ts) {
  if (!ts) return '—'
  return new Date(ts).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })
}

function Stars({ value, onChange }) {
  return (
    <div style={{ display:'flex', gap:'3px' }}>
      {[1,2,3,4,5].map(n => (
        <button key={n} type="button" onClick={() => onChange(n)}
          style={{ width:'22px', height:'22px', borderRadius:'6px', border:'none', cursor:'pointer', fontSize:'11px', fontWeight:700,
            background: n <= value ? 'rgba(30,144,255,0.25)' : 'rgba(var(--fg-rgb),0.06)',
            color: n <= value ? '#5aabff' : 'rgba(var(--fg-rgb),var(--a25))' }}>
          {n}
        </button>
      ))}
    </div>
  )
}

export default function Candidates({ profile }) {
  const isSales = profile.role === 'sales'
  const canAssign = profile.role === 'admin'

  const [candidates, setCandidates] = useState([])
  const [staff, setStaff] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [expandedId, setExpandedId] = useState(null)
  const [err, setErr] = useState('')

  const [uploading, setUploading] = useState(false)
  const [uploadMsg, setUploadMsg] = useState(null)
  const [pendingCandidate, setPendingCandidate] = useState(null) // pre-filled confirm form
  const fileRef = useRef()

  const [zoomPrompt, setZoomPrompt] = useState(null) // { candidate }
  const [zoomDate, setZoomDate] = useState('')
  const [zoomTime, setZoomTime] = useState('')
  const [zoomBusy, setZoomBusy] = useState(false)

  const [scorecards, setScorecards] = useState({}) // candidateId -> [rows]
  const [scoreForm, setScoreForm] = useState({ round:'Screening', communication_rating:0, technical_rating:0, culture_fit_rating:0, recommendation:'Maybe', notes:'' })
  const [savingScore, setSavingScore] = useState(false)

  const fetchCandidates = useCallback(async () => {
    setLoading(true)
    let query = supabase.from('candidates').select('*').order('created_at', { ascending: false })
    if (isSales) query = query.eq('assigned_to', profile.id)
    const { data, error } = await query
    if (error) setErr(error.message)
    else setCandidates(data || [])
    setLoading(false)
  }, [isSales, profile.id])

  useEffect(() => { fetchCandidates() }, [fetchCandidates])

  useEffect(() => {
    if (!canAssign) return
    supabase.from('profiles').select('id,full_name,role').in('role', ['sales','admin']).order('full_name')
      .then(({ data }) => setStaff(data || []))
  }, [canAssign])

  async function loadScorecards(candidateId) {
    const { data } = await supabase.from('candidate_scorecards').select('*').eq('candidate_id', candidateId).order('created_at', { ascending: false })
    setScorecards(prev => ({ ...prev, [candidateId]: data || [] }))
  }

  function toggleExpand(c) {
    const next = expandedId === c.id ? null : c.id
    setExpandedId(next)
    if (next) loadScorecards(c.id)
  }

  // ── CV upload → parse → confirm form ──────────────────────────────────
  async function handleFile(file) {
    if (!file) return
    const ok = /\.(pdf|docx?|DOC|DOCX|PDF)$/.test(file.name)
    if (!ok) { setUploadMsg({ type:'err', text:'Please upload a PDF, DOC, or DOCX file.' }); return }

    setUploading(true); setUploadMsg(null)
    try {
      const path = `cv/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`
      const { error: upErr } = await supabase.storage.from('candidate-cvs').upload(path, file, { contentType: file.type })
      if (upErr) throw upErr
      const { data: { publicUrl } } = supabase.storage.from('candidate-cvs').getPublicUrl(path)

      const { data: { session: parseSession } } = await supabase.auth.getSession()
      const parseRes = await fetch('/api/parse-cv', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileUrl: publicUrl, fileName: file.name, accessToken: parseSession?.access_token }),
      })
      const parsed = await parseRes.json()

      setPendingCandidate({
        full_name: parsed.full_name || '',
        email: parsed.email || '',
        phone: parsed.phone || '',
        position_applied: '',
        cv_url: publicUrl,
        cv_filename: file.name,
      })
      setUploadMsg({ type:'ok', text: parseRes.ok ? '✓ CV parsed — review details below before saving.' : 'CV uploaded, but auto-parse failed — fill in details manually.' })
    } catch (ex) {
      setUploadMsg({ type:'err', text: ex.message || 'Upload failed.' })
    }
    setUploading(false)
    if (fileRef.current) fileRef.current.value = ''
  }

  async function saveCandidate(e) {
    e.preventDefault()
    if (!pendingCandidate.full_name.trim()) { setUploadMsg({ type:'err', text:'Name is required.' }); return }
    const { error } = await supabase.from('candidates').insert({
      full_name: pendingCandidate.full_name.trim(),
      email: pendingCandidate.email.trim() || null,
      phone: pendingCandidate.phone.trim() || null,
      position_applied: pendingCandidate.position_applied.trim() || null,
      cv_url: pendingCandidate.cv_url,
      cv_filename: pendingCandidate.cv_filename,
      status: 'Applied',
      assigned_to: isSales ? profile.id : null,
      created_by: profile.id,
    })
    if (error) { setUploadMsg({ type:'err', text: error.message }); return }
    setPendingCandidate(null)
    setUploadMsg({ type:'ok', text:'✓ Candidate added!' })
    fetchCandidates()
    setTimeout(() => setUploadMsg(null), 3000)
  }

  // ── Status + Zoom ──────────────────────────────────────────────────────
  async function updateStatus(candidate, status) {
    if (status === '2nd Round') {
      setZoomPrompt({ candidate })
      setZoomDate(''); setZoomTime('')
      return
    }
    await supabase.from('candidates').update({ status }).eq('id', candidate.id)
    setCandidates(prev => prev.map(c => c.id === candidate.id ? { ...c, status } : c))
  }

  async function confirmZoomAndAdvance() {
    if (!zoomDate) return
    setZoomBusy(true)
    try {
      const { data: { session: zoomSession } } = await supabase.auth.getSession()
      const res = await fetch('/api/meetings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          topic: `Interview - ${zoomPrompt.candidate.full_name} (2nd Round)`,
          date: zoomDate,
          time: zoomTime || undefined,
          accessToken: zoomSession?.access_token,
        }),
      })
      const meeting = await res.json()
      if (!meeting.success) throw new Error(meeting.error || 'Failed to create the meeting')

      const updates = {
        status: '2nd Round',
        zoom_join_url: meeting.joinUrl,
        zoom_start_url: meeting.startUrl,
        zoom_meeting_id: String(meeting.meetingId),
        zoom_scheduled_at: new Date(`${zoomDate}T${zoomTime || '09:00'}:00`).toISOString(),
      }
      await supabase.from('candidates').update(updates).eq('id', zoomPrompt.candidate.id)
      setCandidates(prev => prev.map(c => c.id === zoomPrompt.candidate.id ? { ...c, ...updates } : c))
      setZoomPrompt(null)
    } catch (ex) {
      setErr(ex.message)
    }
    setZoomBusy(false)
  }

  async function assignCandidate(id, userId) {
    await supabase.from('candidates').update({ assigned_to: userId || null }).eq('id', id)
    setCandidates(prev => prev.map(c => c.id === id ? { ...c, assigned_to: userId || null } : c))
  }

  async function saveScorecard(candidateId) {
    setSavingScore(true)
    const { error } = await supabase.from('candidate_scorecards').insert({
      candidate_id: candidateId,
      round: scoreForm.round,
      interviewer_id: profile.id,
      interviewer_name: profile.full_name || profile.email,
      communication_rating: scoreForm.communication_rating || null,
      technical_rating: scoreForm.technical_rating || null,
      culture_fit_rating: scoreForm.culture_fit_rating || null,
      recommendation: scoreForm.recommendation,
      notes: scoreForm.notes.trim() || null,
    })
    if (!error) {
      setScoreForm({ round:'Screening', communication_rating:0, technical_rating:0, culture_fit_rating:0, recommendation:'Maybe', notes:'' })
      loadScorecards(candidateId)
    } else setErr(error.message)
    setSavingScore(false)
  }

  // ── Filtering ──────────────────────────────────────────────────────────
  const today = new Date().toISOString().slice(0,10)
  const filtered = candidates.filter(c => {
    if (filterStatus !== 'all' && c.status !== filterStatus) return false
    if (search) {
      const q = search.toLowerCase()
      return (c.full_name||'').toLowerCase().includes(q) || (c.email||'').toLowerCase().includes(q) || (c.phone||'').includes(q)
    }
    return true
  })

  const total = candidates.length
  const appliedToday = candidates.filter(c => (c.created_at||'').slice(0,10) === today).length
  const inSecondRound = candidates.filter(c => c.status === '2nd Round').length
  const hired = candidates.filter(c => c.status === 'Hired').length

  return (
    <div>
      <Grid4>
        <MetricCard label="Total Candidates" value={total} />
        <MetricCard label="Applied Today" value={appliedToday} />
        <MetricCard label="In 2nd Round" value={inSecondRound} />
        <MetricCard label="Hired" value={hired} />
      </Grid4>

      {err && <div style={{ padding:'8px 12px', background:'rgba(239,68,68,0.1)', color:'#f87171', borderRadius:'8px', fontSize:'13px', marginBottom:'10px' }}>{err}</div>}

      {/* ── Upload CV ── */}
      <div style={{ ...S.card, marginBottom:'16px' }}>
        <div style={{ fontSize:'13px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a70))', marginBottom:'12px' }}>📤 Upload Candidate CV</div>
        <div
          onClick={() => !uploading && fileRef.current?.click()}
          style={{ border:'1.5px dashed rgba(var(--fg-rgb),0.15)', borderRadius:'12px', padding:'24px', textAlign:'center', cursor: uploading ? 'default' : 'pointer', background:'rgba(var(--fg-rgb),0.02)' }}>
          <div style={{ fontSize:'26px', marginBottom:'6px' }}>📄</div>
          <div style={{ fontSize:'13px', color:'rgba(var(--fg-rgb),var(--a55))', fontWeight:600 }}>{uploading ? 'Uploading & parsing…' : 'Click to upload a CV (PDF, DOC, DOCX)'}</div>
          <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" style={{ display:'none' }} onChange={e => handleFile(e.target.files[0])} />
        </div>

        {uploadMsg && (
          <div style={{ marginTop:'10px', padding:'9px 13px', borderRadius:'9px', fontSize:'13px', background: uploadMsg.type==='ok' ? 'rgba(52,211,153,0.1)' : 'rgba(239,68,68,0.1)', color: uploadMsg.type==='ok' ? '#34d399' : '#f87171' }}>
            {uploadMsg.text}
          </div>
        )}

        {pendingCandidate && (
          <form onSubmit={saveCandidate} style={{ marginTop:'14px', display:'grid', gap:'10px', gridTemplateColumns:'1fr 1fr', padding:'14px', background:'rgba(30,144,255,0.05)', border:'0.5px solid rgba(30,144,255,0.2)', borderRadius:'12px' }}>
            <input style={S.input} placeholder="Full Name *" value={pendingCandidate.full_name} onChange={e => setPendingCandidate({ ...pendingCandidate, full_name: e.target.value })} />
            <input style={S.input} placeholder="Phone" value={pendingCandidate.phone} onChange={e => setPendingCandidate({ ...pendingCandidate, phone: e.target.value })} />
            <input style={S.input} placeholder="Email" value={pendingCandidate.email} onChange={e => setPendingCandidate({ ...pendingCandidate, email: e.target.value })} />
            <input style={S.input} placeholder="Position Applied For" value={pendingCandidate.position_applied} onChange={e => setPendingCandidate({ ...pendingCandidate, position_applied: e.target.value })} />
            <div style={{ gridColumn:'1 / -1', display:'flex', gap:'8px' }}>
              <button type="submit" style={S.btn()}>Save Candidate</button>
              <button type="button" style={S.btn('ghost')} onClick={() => setPendingCandidate(null)}>Cancel</button>
            </div>
          </form>
        )}
      </div>

      {/* ── Filters ── */}
      <div style={{ display:'flex', gap:'8px', marginBottom:'14px', flexWrap:'wrap' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Search name, phone, email…"
          style={{ ...S.input, flex:1, minWidth:'200px' }} />
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ ...S.input, cursor:'pointer' }}>
          <option value="all">All Status</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* ── List ── */}
      {loading ? (
        <div style={{ textAlign:'center', padding:'40px', color:'rgba(var(--fg-rgb),var(--a30))' }}>Loading candidates…</div>
      ) : filtered.length === 0 ? (
        <Empty msg="No candidates yet. Upload a CV above to get started." />
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
          {filtered.map(c => {
            const meta = STATUS_META[c.status] || STATUS_META['Applied']
            const isOpen = expandedId === c.id
            const assignedStaff = staff.find(s => s.id === c.assigned_to)
            return (
              <div key={c.id} style={{ background: isOpen ? 'rgba(var(--fg-rgb),0.06)' : 'rgba(var(--fg-rgb),0.03)', border:`0.5px solid ${isOpen ? 'rgba(30,144,255,0.3)' : 'rgba(var(--fg-rgb),0.08)'}`, borderRadius:'14px', overflow:'hidden' }}>
                <div onClick={() => toggleExpand(c)} style={{ display:'flex', alignItems:'center', gap:'12px', padding:'13px 16px', cursor:'pointer', flexWrap:'wrap' }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:700, fontSize:'14px', color:'var(--fg-solid)' }}>{c.full_name || '—'}</div>
                    <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a35))', marginTop:'1px' }}>
                      {c.position_applied || 'No position specified'} {assignedStaff && `· Assigned to ${assignedStaff.full_name}`}
                    </div>
                  </div>
                  {c.zoom_join_url && c.status === '2nd Round' && <ZoomBtn link={c.zoom_join_url} />}
                  <span style={{ fontSize:'10px', fontWeight:700, padding:'3px 10px', borderRadius:'20px', background:meta.bg, color:meta.color, whiteSpace:'nowrap' }}>{c.status}</span>
                  <div style={{ fontSize:'10px', color:'rgba(var(--fg-rgb),var(--a20))' }}>{isOpen ? '▲' : '▼'}</div>
                </div>

                {isOpen && (
                  <div style={{ borderTop:'0.5px solid rgba(var(--fg-rgb),0.07)', padding:'16px 20px', background:'rgba(0,0,0,0.15)', display:'flex', flexDirection:'column', gap:'16px' }}>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'8px' }}>
                      {[
                        { label:'Phone', value:c.phone, href: c.phone ? `tel:${c.phone}` : null },
                        { label:'Email', value:c.email, href: c.email ? `mailto:${c.email}` : null },
                        { label:'CV', value: c.cv_url ? 'View File' : '—', href:c.cv_url },
                        { label:'Applied', value: fmtDate(c.created_at) },
                        { label:'Position', value: c.position_applied || '—' },
                        { label:'Meeting Link', value: c.zoom_join_url ? 'Join' : '—', href:c.zoom_join_url },
                      ].map(item => (
                        <div key={item.label} style={{ background:'rgba(var(--fg-rgb),0.04)', borderRadius:'10px', padding:'8px 10px' }}>
                          <div style={{ fontSize:'9px', color:'rgba(var(--fg-rgb),var(--a30))', marginBottom:'3px', textTransform:'uppercase' }}>{item.label}</div>
                          {item.href
                            ? <a href={item.href} target={item.href.startsWith('http') ? '_blank' : undefined} rel="noreferrer" style={{ fontSize:'13px', color:'#60a5fa', fontWeight:600, textDecoration:'none' }}>{item.value}</a>
                            : <div style={{ fontSize:'13px', color:'rgba(var(--fg-rgb),var(--a70))' }}>{item.value || '—'}</div>}
                        </div>
                      ))}
                    </div>

                    {canAssign && (
                      <div>
                        <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a40))', marginBottom:'6px' }}>Assign To</div>
                        <select value={c.assigned_to || ''} onChange={e => assignCandidate(c.id, e.target.value)} style={{ ...S.input, cursor:'pointer' }}>
                          <option value="">Unassigned</option>
                          {staff.map(s => <option key={s.id} value={s.id}>{s.full_name} ({s.role})</option>)}
                        </select>
                      </div>
                    )}

                    <div>
                      <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a40))', marginBottom:'8px' }}>Update Status</div>
                      <div style={{ display:'flex', gap:'6px', flexWrap:'wrap' }}>
                        {STATUSES.map(s => (
                          <button key={s} onClick={() => updateStatus(c, s)}
                            style={{ padding:'5px 12px', borderRadius:'8px', fontSize:'11px', fontWeight:700, cursor:'pointer',
                              background: c.status === s ? (STATUS_META[s]?.bg) : 'rgba(var(--fg-rgb),0.06)',
                              color: c.status === s ? (STATUS_META[s]?.color) : 'rgba(var(--fg-rgb),var(--a40))',
                              border: c.status === s ? `1px solid ${STATUS_META[s]?.color}` : '1px solid transparent' }}>
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* ── Scorecard ── */}
                    <div>
                      <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a40))', marginBottom:'8px' }}>Interview Scorecard</div>
                      <div style={{ background:'rgba(var(--fg-rgb),0.04)', borderRadius:'12px', padding:'14px', display:'flex', flexDirection:'column', gap:'10px' }}>
                        <select value={scoreForm.round} onChange={e => setScoreForm({ ...scoreForm, round: e.target.value })} style={{ ...S.input, cursor:'pointer', width:'160px' }}>
                          {['Screening','1st Round','2nd Round'].map(r => <option key={r} value={r}>{r}</option>)}
                        </select>

                        {RATING_FIELDS.map(f => (
                          <div key={f.key} style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                            <div style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a60))', width:'110px' }}>{f.label}</div>
                            <Stars value={scoreForm[f.key]} onChange={v => setScoreForm({ ...scoreForm, [f.key]: v })} />
                          </div>
                        ))}

                        <div style={{ display:'flex', gap:'6px' }}>
                          {RECOMMENDATIONS.map(r => (
                            <button key={r} type="button" onClick={() => setScoreForm({ ...scoreForm, recommendation: r })}
                              style={{ padding:'6px 14px', borderRadius:'8px', fontSize:'11px', fontWeight:700, cursor:'pointer',
                                background: scoreForm.recommendation === r ? 'rgba(30,144,255,0.2)' : 'rgba(var(--fg-rgb),0.06)',
                                color: scoreForm.recommendation === r ? '#5aabff' : 'rgba(var(--fg-rgb),var(--a40))', border:'none' }}>
                              {r}
                            </button>
                          ))}
                        </div>

                        <textarea value={scoreForm.notes} onChange={e => setScoreForm({ ...scoreForm, notes: e.target.value })} rows={3} placeholder="Interview notes…"
                          style={{ ...S.input, resize:'vertical' }} />

                        <button onClick={() => saveScorecard(c.id)} disabled={savingScore} style={{ ...S.btn(), width:'160px', opacity: savingScore ? 0.6 : 1 }}>
                          {savingScore ? 'Saving…' : 'Save Scorecard'}
                        </button>
                      </div>

                      {(scorecards[c.id] || []).length > 0 && (
                        <div style={{ marginTop:'10px', display:'flex', flexDirection:'column', gap:'6px' }}>
                          {scorecards[c.id].map(sc => (
                            <div key={sc.id} style={{ padding:'10px 12px', background:'rgba(var(--fg-rgb),0.03)', border:'0.5px solid rgba(var(--fg-rgb),0.06)', borderRadius:'10px' }}>
                              <div style={{ display:'flex', justifyContent:'space-between', fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a40))' }}>
                                <span>{sc.round} · {sc.interviewer_name}</span>
                                <span>{fmtDate(sc.created_at)}</span>
                              </div>
                              <div style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a70))', marginTop:'4px' }}>
                                💬 {sc.communication_rating||'—'} · 🛠 {sc.technical_rating||'—'} · 🤝 {sc.culture_fit_rating||'—'} ·
                                <strong style={{ color: sc.recommendation === 'Hire' ? '#34d399' : sc.recommendation === 'No Hire' ? '#f87171' : '#fbbf24', marginLeft:'4px' }}>{sc.recommendation}</strong>
                              </div>
                              {sc.notes && <div style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a50))', marginTop:'4px' }}>{sc.notes}</div>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* ── Zoom scheduling modal ── */}
      {zoomPrompt && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}>
          <div style={{ background:'var(--bg-page)', border:'1px solid rgba(30,144,255,0.3)', borderRadius:'16px', padding:'26px', width:'100%', maxWidth:'380px' }}>
            <div style={{ fontSize:'16px', fontWeight:800, color:'var(--fg-solid)', marginBottom:'6px' }}>Schedule 2nd Round Meeting</div>
            <div style={{ fontSize:'13px', color:'rgba(var(--fg-rgb),var(--a50))', marginBottom:'16px' }}>
              For <strong style={{ color:'var(--fg-solid)' }}>{zoomPrompt.candidate.full_name}</strong>. This creates a real Google Meet meeting.
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
              <input type="date" value={zoomDate} onChange={e => setZoomDate(e.target.value)} style={S.input} />
              <input type="time" value={zoomTime} onChange={e => setZoomTime(e.target.value)} style={S.input} />
            </div>
            <div style={{ display:'flex', gap:'10px', marginTop:'18px' }}>
              <button onClick={confirmZoomAndAdvance} disabled={zoomBusy || !zoomDate} style={{ ...S.btn(), flex:1, opacity: zoomBusy || !zoomDate ? 0.6 : 1 }}>
                {zoomBusy ? 'Creating…' : 'Create Meeting'}
              </button>
              <button onClick={() => setZoomPrompt(null)} style={S.btn('ghost')}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
